/* eslint-disable no-console */
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { firstBy } from 'thenby';
import {
    HelperMethods,
    SearchOrderResponse,
    ClinicalFeedbackDetailsInjectData,
    feedbackTypeOptionList,
    seriousIncidentTypeUki,
    seriousIncidentTypeAnz
} from '../clinical-feedback.model';
import { KeyValuePair } from '@app/shared/model/shared.model';
import { CommonUtilities } from 'src/app/shared/common-utilities';
import { DateTime } from 'luxon';
import { MatDialog } from '@angular/material/dialog';
import { NewClinicalFeedbackComponent } from '../new-clinical-feedback/new-clinical-feedback.component';
import { AwsS3ObjectPreSignedUrl, FeedbackStatus, Region } from '@app/api-module/control-centre-api-client';
import { SnackbarService } from '@app/services/snackbar.sevice';
import { ClinicalFeedbackConstants } from '../clinical-feedback-constants';
import { catchError, Observable, Subscription, throwError, TimeoutError } from 'rxjs';
import { ClinicalFeedbackService } from '../clinical-feedback.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Constants } from '@app/models/constants';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-clinical-feedback-table',
    templateUrl: './clinical-feedback-table.component.html',
    styleUrls: ['./clinical-feedback-table.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ClinicalFeedbackTableComponent implements OnChanges, OnDestroy {
    @Input()
    studyItems: SearchOrderResponse[] | null = [];

    @Input()
    searchApplied = false;

    @Input()
    searchOrderOverLimit = false;

    @Output()
    refreshFeedbackTable = new EventEmitter();

    @ViewChild(MatSort) sort!: MatSort;

    private readonly subscriptions = new Subscription();

    FeedbackStatus = FeedbackStatus;
    feedbackTypeOptionList = feedbackTypeOptionList;
    seriousIncidentTypeAnz = seriousIncidentTypeAnz;
    seriousIncidentTypeUki = seriousIncidentTypeUki;
    commonUtilities = CommonUtilities;
    clinicalFeedbackConstants = ClinicalFeedbackConstants;

    displayedColumns: string[] = [
        'patientMrn',
        'accessionNumber',
        'feedbackStatus',
        'feedbackType',
        'feedbackSeriousIncidentType',
        'patientFirstName',
        'patientLastName',
        'reportingRadiologist',
        'studyType',
        'studyDate',
        'action'
    ];
    dataSource = new MatTableDataSource<SearchOrderResponse>();

    constructor(
        public dialog: MatDialog,
        private readonly snackbarService: SnackbarService,
        private readonly clinicalFeedbackService: ClinicalFeedbackService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['studyItems']) {
            this.dataSource.data = changes['studyItems'].currentValue ?? [];
            this.dataSource.sort = this.sort;
            this.dataSource.sortData = this.sortStudyItems;
        }
    }

    downloadEverlightResponseDocument(event: Event, accessionNumber: string, documentId: string) {
        event.stopPropagation();
        (document.activeElement as HTMLElement).blur(); // to remove focus from the mat-select, to restore its default appearance
        this.subscriptions.add(
            this.clinicalFeedbackService
                .getDownloadLink(accessionNumber, documentId)
                .pipe(catchError(err => this.getErrorMessage(err)))
                .subscribe({
                    next: this.processDocumentDownloadUrl,
                    error: err => this.snackbarService.openSnackBar(err, SnackbarService.SNACK_BAR_CLASS_ALERT)
                })
        );
    }

    onDownloadFeedbackDocument(item: SearchOrderResponse) {
        if (item.feedbackId) {
            this.subscriptions.add(
                this.clinicalFeedbackService
                    .getDownloadCustomerFeedbackLink(item.feedbackId)
                    .pipe(catchError(err => this.getErrorMessage(err)))
                    .subscribe({
                        next: this.processDocumentDownloadUrl,
                        error: err => this.snackbarService.openSnackBar(err, SnackbarService.SNACK_BAR_CLASS_ALERT)
                    })
            );
        }
    }

    processDocumentDownloadUrl(awsS3PresignedUrl: AwsS3ObjectPreSignedUrl) {
        if (awsS3PresignedUrl !== null) {
            window.open(awsS3PresignedUrl.url, '_self');
        } else {
            this.snackbarService.openSnackBar(ClinicalFeedbackConstants.DOWNLOAD_DOCUMENT_ERROR_MSG, SnackbarService.SNACK_BAR_CLASS_ALERT);
        }
    }

    getTableClass(item: SearchOrderResponse) {
        const cssClass = {};
        if (item.feedbackId) {
            cssClass['clinical-feedback-row'] = true;
        }

        return cssClass;
    }

    sortStudyItems(rows: SearchOrderResponse[], sort: MatSort): SearchOrderResponse[] {
        if (!sort.direction) {
            return rows;
        }
        const fieldName = sort.active,
            isAsc = sort.direction === 'asc';
        return [...rows].sort(
            firstBy((a: SearchOrderResponse, b: SearchOrderResponse) => {
                if (fieldName === 'studyDate') {
                    return CommonUtilities.compareStringDateWithAscOrDescOrder(a[fieldName] as string, b[fieldName] as string, isAsc);
                } else {
                    return CommonUtilities.compareStringWithAscOrDescOrder(a[fieldName] as string, b[fieldName] as string, isAsc);
                }
            }).thenBy((itemA: SearchOrderResponse, itemB: SearchOrderResponse) =>
                CommonUtilities.compareStringDateWithAscOrDescOrder(itemA.studyDate as string, itemB.studyDate as string, isAsc)
            )
        );
    }

    displayDateTime(dateTime: string, showTime?: boolean) {
        return DateTime.fromISO(dateTime).toFormat(showTime ? 'dd/MM/yyyy HH:mm:ss' : 'dd/MM/yyyy');
    }

    raiseClinicalFeedback(event: Event, order: SearchOrderResponse) {
        event.stopPropagation();
        if (order.region === Region.Unknown) {
            this.snackbarService.openSnackBar(
                ClinicalFeedbackConstants.ORDER_WITHOUT_REGION_MESSAGE,
                SnackbarService.SNACK_BAR_CLASS_ALERT
            );
        } else {
            const dialogConfig = HelperMethods.getNewClinicalFeedbackMatDialogConfig();
            dialogConfig.data = new ClinicalFeedbackDetailsInjectData({
                isFromDialog: true,
                order: order
            });

            const dialogReg = this.dialog.open(NewClinicalFeedbackComponent, dialogConfig);

            this.subscriptions.add(
                dialogReg.afterClosed().subscribe(feedbackSaved => {
                    if (feedbackSaved) {
                        this.refreshFeedbackTable.emit();
                    }
                })
            );
        }
    }

    getRegionSeriousIncidentList(region: Region): Array<KeyValuePair> {
        switch (region) {
            case Region.Anz:
                return seriousIncidentTypeAnz;

            case Region.Uki:
                return seriousIncidentTypeUki;

            default:
                console.error(`Unexpected region ${region}.`);
                return [];
        }
    }

    getFeedbackStatusClass(feedbackValue: FeedbackStatus): string {
        switch (feedbackValue) {
            case FeedbackStatus.New:
                return 'feedback-status-badge new';
            case FeedbackStatus.InProgress:
                return 'feedback-status-badge in-progress';
            case FeedbackStatus.ClientInputRequired:
                return 'feedback-status-badge client-input-required';
            case FeedbackStatus.Complete:
                return 'feedback-status-badge complete';
            case FeedbackStatus.Cancelled:
                return 'feedback-status-badge cancelled';
            default:
                return;
        }
    }

    getFeedbackStatusValue(feedbackValue: FeedbackStatus): string {
        switch (feedbackValue) {
            case FeedbackStatus.New:
                return 'New';
            case FeedbackStatus.InProgress:
                return 'In progress';
            case FeedbackStatus.ClientInputRequired:
                return 'Client input required';
            case FeedbackStatus.Complete:
                return 'Complete';
            case FeedbackStatus.Cancelled:
                return 'Cancelled';
            default:
                return;
        }
    }

    getErrorMessage(response: HttpErrorResponse): Observable<never> {
        if (response instanceof TimeoutError) {
            console.error('Request timeout: ', JSON.stringify(response));
            return throwError(() => new Error(Constants.UnableToContactServerMessage));
        }
        if ([403, 400, 404].includes(response.status)) {
            console.error(`Server returned code ${response.status} with body:\n${JSON.stringify(response.error)}`);
            if (response.status === 403) {
                return throwError(() => new Error('You do not have permission to view this feedback response.'));
            }
            if (response.status === 400) {
                // SPA shouldn't submit an invalid form. if it does and server returns error, it's an error scenario.
                const errorMessage =
                    'Something went wrong while getting the feedback response. Please try again in some time. ' +
                    'If the problem persists, please contact Everlight support team';
                return throwError(() => new Error(errorMessage));
            }
            if (response.status === 404) {
                const errorMessage =
                    'Cannot download the feedback response. If the problem persists, please contact Everlight support team';
                return throwError(() => new Error(errorMessage));
            }
        }

        console.error('Unable to contact server: ', response.status);
        return throwError(() => new Error(Constants.UnableToContactServerMessage));
    }

    displayTableMessage() {
        if (this.searchApplied) {
            if (this.dataSource.data?.length === 0 && !this.searchOrderOverLimit) {
                return ClinicalFeedbackConstants.NO_ORDER_SEARCH_RESULT;
            } else if (this.searchOrderOverLimit) {
                return ClinicalFeedbackConstants.SEARCH_ORDER_OVER_LIMIT_MESSAGE;
            }
        } else if (this.dataSource.data?.length === 0) {
            return ClinicalFeedbackConstants.NO_DEFAULT_FEEDBACK;
        }
    }

    public encourageWrapping(s: string): string {
        return s
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/([_/|~^])/g, '$1<wbr/>');
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
